/**
 * InstallmentsPreview
 *
 * Exibe parcelas de pagamento com datas e valores.
 * Modo editável permite alterar vencimentos na aprovação financeira.
 */

import React from 'react';
import { Calendar as CalendarIcon, DollarSign } from 'lucide-react';
import { formatDateOnly, toDateInputValue } from '../../../utils/dateUtils';
import { CalendarDatePicker } from '../Calendar/Calendar';
import './InstallmentsPreview.css';

const InstallmentsPreview = ({
  installments = [],
  totalValue = 0,
  editable = false,
  minDate = '',
  onInstallmentDateChange,
}) => {
  if (!installments || installments.length === 0) {
    return null;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getDueDateValue = (inst) => {
    const raw = inst.due_date ?? inst.dueDate;
    return editable ? toDateInputValue(raw) : formatDateOnly(raw);
  };

  const totalAmount = installments.reduce((sum, inst) => {
    const amount = parseFloat(inst.amount) || 0;
    return sum + (isNaN(amount) ? 0 : amount);
  }, 0);
  const averageAmount = installments.length > 0 && totalAmount > 0
    ? totalAmount / installments.length
    : (totalValue > 0 && installments.length > 0 ? totalValue / installments.length : 0);

  const firstDue = installments[0]?.due_date ?? installments[0]?.dueDate;
  const lastDue = installments[installments.length - 1]?.due_date
    ?? installments[installments.length - 1]?.dueDate;

  return (
    <div className={`installments-preview${editable ? ' is-editable' : ''}`}>
      <div className="installments-preview-header">
        <h4>
          <DollarSign size={16} />
          Parcelas de Pagamento
        </h4>
        <div className="installments-summary">
          <span className="summary-badge">
            {installments.length}x de {formatCurrency(averageAmount)}
          </span>
        </div>
      </div>

      {editable && (
        <p className="installments-editable-hint">
          Ajuste as datas de vencimento conforme necessário antes de aprovar.
        </p>
      )}

      <div className="installments-list">
        {installments.map((inst, idx) => {
          const installmentNumber = inst.installment_number ?? inst.installmentNumber ?? idx + 1;
          const paymentId = inst.id ?? idx;
          const isPending = !inst.status || inst.status === 'pending';

          return (
            <div key={paymentId} className="installment-item">
              <div className="installment-number">
                {installmentNumber}ª
              </div>
              <div className="installment-details">
                <div className="installment-date">
                  {editable && isPending ? (
                    <>
                      <span className="installment-date-label">
                        <CalendarIcon size={14} />
                        Vencimento
                      </span>
                      <CalendarDatePicker
                        value={getDueDateValue(inst)}
                        minDate={minDate}
                        placeholder="Selecionar vencimento"
                        onChange={(newDate) => onInstallmentDateChange?.(paymentId, newDate)}
                      />
                    </>
                  ) : (
                    <>
                      <CalendarIcon size={14} />
                      <span>{getDueDateValue(inst)}</span>
                    </>
                  )}
                </div>
                <div className="installment-amount">
                  {formatCurrency(inst.amount || 0)}
                </div>
              </div>
              {inst.status && (
                <div className={`installment-status status-${inst.status}`} title={
                  inst.status === 'paid' ? 'Pago' :
                  inst.status === 'overdue' ? 'Vencido' :
                  inst.status === 'cancelled' ? 'Cancelado' : 'Pendente'
                }>
                  {inst.status === 'pending' ? '' : (
                    inst.status === 'paid' ? 'Pago' :
                    inst.status === 'overdue' ? 'Vencido' :
                    inst.status === 'cancelled' ? 'Cancelado' : ''
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="installments-footer">
        <div className="footer-item">
          <span className="footer-label">Total:</span>
          <span className="footer-value">{formatCurrency(totalValue || totalAmount)}</span>
        </div>
        {installments.length > 0 && (
          <div className="footer-item">
            <span className="footer-label">Primeira parcela:</span>
            <span className="footer-value">{formatDateOnly(firstDue)}</span>
          </div>
        )}
        {installments.length > 0 && (
          <div className="footer-item">
            <span className="footer-label">Última parcela:</span>
            <span className="footer-value">{formatDateOnly(lastDue)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallmentsPreview;
